import React, { ReactElement } from 'react'
import { isArray } from 'lodash'
import { useTranslation } from 'react-i18next'
import TsxUtils from 'utils/tsx'
import VariableUtils from 'utils/variable'
import {
  FormattedAlter,
  FormattedAlterField,
} from '../pages/Campaign/CampaignDetails/CampaignDetailsHistory/types'
import { HistoryLogAlter, HistoryLogAlterValue } from 'types/campaign'
import { Primitive } from '../types/various'
import DateUtils from './date'
import { DATE_HOUR_FORMAT } from '../constant'

export default (alters: HistoryLogAlter[]): FormattedAlter[] => {
  const { t } = useTranslation()

  const formatField = (value: HistoryLogAlterValue): FormattedAlterField => {
    return valueIsEmpty(value)
      ? {
          value: t('common.empty') as string,
          component: ValueComponent({ empty: true }),
        }
      : {
          value: formatAlterValue(value),
          component: ValueComponent(),
        }
  }

  return (
    alters?.map(alter => {
      const after = formatField(alter.after)
      const before = formatField(alter.before)

      return {
        values: {
          after: after.value,
          attribute: alter.attribute,
          before: before.value,
        },
        components: {
          after: after.component,
          attribute: ValueComponent(),
          before: before.component,
        },
      }
    }) || []
  )
}

const ValueComponent = (options?: { empty: boolean }): ReactElement => (
  <span
    className={TsxUtils.joinClasses(
      'HistoryLogItem__alter-value',
      options?.empty && 'HistoryLogItem__alter-value--empty'
    )}
  />
)

const valueIsEmpty = (value: unknown): boolean => {
  return (
    value === '' ||
    VariableUtils.isNullOrUndefined(value) ||
    (isArray(value) && VariableUtils.isEmptyArray(value))
  )
}

const formatAlterValue = <Value extends Primitive | Value[]>(value: Value): string => {
  if (isArray(value)) {
    if (isDateRange(value)) return formatDateRange(value)

    return value.map(formatAlterValue).join(', ')
  }

  if (isDate(value)) {
    return formatDate(value)
  }

  if (typeof value === 'object' && value !== null) {
    const firstKey = Object.keys(value)[0]
    const isFirstValueArray = isArray(value[firstKey])
    const secondKey = Object.keys(value)[1]

    if (firstKey && isFirstValueArray && !secondKey) {
      const firstValue = value[firstKey] as unknown[]

      const transformed = firstValue.join(', ')

      return `{ ${firstKey}: ${transformed} }`
    }

    return JSON.stringify(value)
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return value?.toString() || ''
}

const formatDateRange = (value: string[]): string => {
  return `${formatDate(value[0])} → ${formatDate(value[1])}`
}

const formatDate = (value: string): string => DateUtils.parseAndFormat(value, DATE_HOUR_FORMAT)

const isDateRange = (value: unknown[]): value is string[] => {
  return value.length === 2 && isDate(value[0]) && isDate(value[1])
}

const isDate = (value: unknown): value is string => {
  const DATE_REGEX = /^(\d{1,4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2}\.\d{3})\+(\d{2}:\d{2})$/

  return typeof value === 'string' && DATE_REGEX.test(value)
}
